import React, { useEffect, useState } from "react";
import StatisticWidget from "../components/Widget/Statistic.jsx";
import AchievementWidget from "../components/Widget/Achievment.jsx";
import DashboardHeader from "../components/Other/DashboardHeader.jsx";
import ScrolledCard from "../components/Widget/ScrolledCard.jsx";
import HistoryModal from "../components/Widget/HistoryModal.jsx";
import { useOutletContext } from "react-router-dom";
import Footer from "../components/Footer.jsx";
import { useAuth } from "../context/AuthContext.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faShieldAlt, faUsers, faChartLine } from "@fortawesome/free-solid-svg-icons";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Avatar from "../assets/images/avatar.jpg";

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [dailyCount, setDailyCount] = useState(0);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [diagnosesAfterCutoffCount, setDiagnosesAfterCutoffCount] = useState(0);
  const [vaccinationsAfterCutoffCount, setVaccinationsAfterCutoffCount] = useState(0); // Vaccinations after cutoff date
  const [maleCount, setMaleCount] = useState(0);
  const [femaleCount, setFemaleCount] = useState(0);
  const [monitoringCount, setMonitoringCount] = useState(0);
  const [screeningCount, setScreeningCount] = useState(0);
  const [rftCount, setRftCount] = useState(0);
  const [vaccinationsCount, setVaccinationsCount] = useState(0);
  const [diagnosesCount, setDiagnosesCount] = useState(0);
  const { userNames } = useAuth();

  const [sidebarToggle] = useOutletContext();
  const [nafsProjectData, setNafsProjectData] = useState([]);
  const [showOverall, setShowOverall] = useState(false);
  const [showNafs, setShowNafs] = useState(false);

  // History Modal State
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    data: [],
    label: "",
  });

  const tollFreeHistory = [1160, 1230, 1185, 1205, 1170, 1220, 1100, 1195, 1240, 1150, 1210, 1180, 1253];
  const safetyNetHistory = [2030, 2140, 2060, 2110, 2070, 2130, 2010, 2090, 2150, 2080, 2120, 2050, 2170];

  const overallTollCount = 15503;
  const totalSafetyNetCount = 27220;
  const beneficiariesTotal = overallTollCount + totalSafetyNetCount;

  const handleOpenHistory = (type) => {
    if (type === "toll") {
      setModalConfig({
        isOpen: true,
        title: "TOLL FREE Patient History (Last 13 Months)",
        data: tollFreeHistory,
        label: "Toll Free Patients",
      });
    } else {
      setModalConfig({
        isOpen: true,
        title: "SAFETYNET Patient History (Last 13 Months)",
        data: safetyNetHistory,
        label: "SafetyNet Patients",
      });
    }
  };

  const [antenantalsAfterCutoffCount, setAntenantalsAfterCutoffCount] = useState(0);
  const [renalHistoriesAfterCutoffCount, setRenalHistoriesAfterCutoffCount] = useState(0);
  const [rftsAfterCutoffCount, setRFTsAfterCutoffCount] = useState(0);
  const [monitoringsAfterCutoffCount, setMonitoringsAfterCutoffCount] = useState(0);
  const [drugsAfterCutoffCount, setDrugsAfterCutoffCount] = useState(0);

  const cutOffDate = new Date("2024-10-07T00:00:00Z");
  const endDate = new Date("2025-05-01T23:59:59Z");

  const fetchData = async () => {
    try {
      // Fetch the all-patients API
      const patientsResponse = await fetch(
        "https://mk-be-strapi-production.up.railway.app/api/all-patients"
      );
      const patientsData = await patientsResponse.json();
  
      // Extract and flatten patients data
      const allPatients = patientsData.map((user) => user.patients).flat();
      setTotalPatients(allPatients.length);
  
      // Filter patients created within the date range (Oct 2024 - May 2025)
      const filteredNafsProjectData = allPatients.filter((patient) => {
        const patientDate = new Date(patient.createdAt);
        return patientDate >= cutOffDate && patientDate <= endDate;
      });
  
      const totalNafsPatients = filteredNafsProjectData.length;
  
      // Count diagnoses added within the date range (regardless of patient registration date)
      const diagnosesAfterCutoff = allPatients.reduce((count, patient) => {
        const patientDiagnoses = patient.diagnoses || [];
        const validDiagnoses = patientDiagnoses.filter((diagnosis) => {
          const diagnosisDate = new Date(diagnosis.createdAt);
          return diagnosisDate >= cutOffDate && diagnosisDate <= endDate;
        });
        return count + validDiagnoses.length;
      }, 0);
      setDiagnosesAfterCutoffCount(diagnosesAfterCutoff);
  
      // Count vaccinations added within the date range (regardless of patient registration date)
      const vaccinationsAfterCutoff = allPatients.reduce((count, patient) => {
        const patientVaccinations = patient.vaccinations || [];
        const validVaccinations = patientVaccinations.filter((vaccination) => {
          const vaccinationDate = new Date(vaccination.createdAt);
          return vaccinationDate >= cutOffDate && vaccinationDate <= endDate;
        });
        return count + validVaccinations.length;
      }, 0);
      setVaccinationsAfterCutoffCount(vaccinationsAfterCutoff);
  
      // Count antenatals added within the date range
      const antenantalsAfterCutoff = allPatients.reduce((count, patient) => {
        const patientAntenantals = patient.antenantals || [];
        const validAntenantals = patientAntenantals.filter((antenatal) => {
          const antenatalDate = new Date(antenatal.createdAt);
          return antenatalDate >= cutOffDate && antenatalDate <= endDate;
        });
        return count + validAntenantals.length;
      }, 0);
      setAntenantalsAfterCutoffCount(antenantalsAfterCutoff);
  
      // Count renal histories added within the date range
      const renalHistoriesAfterCutoff = allPatients.reduce((count, patient) => {
        const patientRenalHistories = patient.renal_histories || [];
        const validRenalHistories = patientRenalHistories.filter((renalHistory) => {
          const renalHistoryDate = new Date(renalHistory.createdAt);
          return renalHistoryDate >= cutOffDate && renalHistoryDate <= endDate;
        });
        return count + validRenalHistories.length;
      }, 0);
      setRenalHistoriesAfterCutoffCount(renalHistoriesAfterCutoff);
  
      // Count RFTs added within the date range
      const rftsAfterCutoff = allPatients.reduce((count, patient) => {
        const patientRFTs = patient.rfts || [];
        const validRFTs = patientRFTs.filter((rft) => {
          const rftDate = new Date(rft.createdAt);
          return rftDate >= cutOffDate && rftDate <= endDate;
        });
        return count + validRFTs.length;
      }, 0);
      setRFTsAfterCutoffCount(rftsAfterCutoff);
  
      // Count monitorings added within the date range
      const monitoringsAfterCutoff = allPatients.reduce((count, patient) => {
        const patientMonitorings = patient.monitorings || [];
        const validMonitorings = patientMonitorings.filter((monitoring) => {
          const monitoringDate = new Date(monitoring.createdAt);
          return monitoringDate >= cutOffDate && monitoringDate <= endDate;
        });
        return count + validMonitorings.length;
      }, 0);
      setMonitoringsAfterCutoffCount(monitoringsAfterCutoff);
  
      // Count drugs added within the date range
      const drugsAfterCutoff = allPatients.reduce((count, patient) => {
        const patientDrugs = patient.drugs || [];
        const validDrugs = patientDrugs.filter((drug) => {
          const drugDate = new Date(drug.createdAt);
          return drugDate >= cutOffDate && drugDate <= endDate;
        });
        return count + validDrugs.length;
      }, 0);
      setDrugsAfterCutoffCount(drugsAfterCutoff);
  
      // Update state for other metrics like monthly, weekly, daily, gender counts, etc.
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const monthlyData = allPatients.filter((patient) => {
        const patientDate = new Date(patient.createdAt);
        return patientDate.getMonth() + 1 === currentMonth;
      });
      setMonthlyCount(monthlyData.length);
  
      const currentWeekStart = new Date(currentDate);
      currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay());
      currentWeekStart.setHours(0, 0, 0);
      const endOfWeek = new Date(currentWeekStart);
      endOfWeek.setDate(currentWeekStart.getDate() + 6);
      endOfWeek.setHours(23, 59, 59);
  
      const weeklyData = allPatients.filter((patient) => {
        const patientDate = new Date(patient.createdAt);
        return patientDate >= currentWeekStart && patientDate <= endOfWeek;
      });
      setWeeklyCount(weeklyData.length);
  
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59);
  
      const dailyData = allPatients.filter((patient) => {
        const patientDate = new Date(patient.createdAt);
        return patientDate >= startOfDay && patientDate <= endOfDay;
      });
      setDailyCount(dailyData.length);
  
      const malePatients = allPatients.filter(
        (patient) => patient.sex === "Male"
      );
      setMaleCount(malePatients.length);
  
      const femalePatients = allPatients.filter(
        (patient) => patient.sex === "Female"
      );
      setFemaleCount(femalePatients.length);
  
      // Combine both total patients and total data into the NAFS data array
      setNafsProjectData([
        { title: "Total Patients", count: totalNafsPatients },
        { title: "Total Diagnoses", count: diagnosesAfterCutoff },
        { title: "Total Vaccinations", count: 457 },
        { title: "Total Antenatals", count: antenantalsAfterCutoff },
        { title: "Total Renal Histories", count: renalHistoriesAfterCutoff },
        { title: "Total RFTs", count: rftsAfterCutoff },
        { title: "Total Monitorings", count: monitoringsAfterCutoff },
        { title: "Total Drugs", count: drugsAfterCutoff },
      ]);
  
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const avatar =
    "https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80";



  const dataOS = [
    {
      title: "Daily Enrollments",
      count: dailyCount,
      color: "cardSuccess",
    },
    {
      title: "Monthly Enrollments",
      count: monthlyCount,
      color: "cardWarning",
    },
    {
      title: "Weekly Enrollments",
      count: weeklyCount,
      color: "cardLime",
    },
    {
      title: "Female Patients",
      count: femaleCount,
      color: "cardDanger",
    },
    {
      title: "Male Patients",
      count: maleCount,
      color: "cardInfo",
    },
  ];

  const servicesOs = [
    {
      title: "Number of Monitorings",
      count: monitoringCount,
      color: "cardLime",
    },
    {
      title: "Renal Functional Tests",
      count: rftCount,
      color: "cardDanger",
    },
    {
      title: "Number of Screenings",
      count: screeningCount,
      color: "cardSuccess",
    },
    {
      title: "Number of Vaccinations",
      count: vaccinationsCount,
      color: "cardInfo",
    },
    {
      title: "Number of Diagnoses",
      count: diagnosesCount,
      color: "cardWarning",
    },
  ];

  const overallData = [
    {
      title: "Total Patients",
      count: totalPatients,
    },
    {
      title: "Total Female Patients",
      count: femaleCount,
    },
    {
      title: "Total Male Patients",
      count: maleCount,
    },
    {
      title: "Total Monitoring",
      count: monitoringCount,
    },
    {
      title: "Total Screening",
      count: screeningCount,
    },
    {
      title: "Total RFTs",
      count: rftCount,
    },
    {
      title: "Total Vaccinations",
      count: vaccinationsCount,
    },
    {
      title: "Total Diagnoses",
      count: diagnosesCount,
    },
  ];

  const nafsData = [
    {
      title: "Total Vaccinations",
      count: vaccinationsCount,
    },
    {
      title: "Total Antenantals",
      count: nafsProjectData.reduce((acc, patient) => acc + (patient.antenantals ? patient.antenantals.length : 0), 0),
    },
    {
      title: "Total Renal Histories",
      count: nafsProjectData.reduce((acc, patient) => acc + (patient.renal_histories ? patient.renal_histories.length : 0), 0),
    },
    {
      title: "Total Diagnoses",
      count: diagnosesCount,
    },
    {
      title: "Total Monitorings",
      count: monitoringCount,
    },
    {
      title: "Total RFTs",
      count: rftCount,
    },
    {
      title: "Total Drugs",
      count: nafsProjectData.reduce((acc, patient) => {
        return acc + (patient.drugs ? patient.drugs.length : 0);
      }, 0),
    },
  ];


  return (
    <>
      <main className="h-full">
        <DashboardHeader
          toggle={sidebarToggle}
          avatar={Avatar}
          user={{ name: userNames }}
        />
        <div className="px-2 mx-auto mainCard">
          <div className="w-full overflow-hidden text-slate-700 md:grid gap-4 grid md:grid-cols-6">
            <StatisticWidget className="col-span-4 col-start-1 bg-white" />
            <AchievementWidget count={beneficiariesTotal} />
          </div>
        </div>

        {/* Project Statistics Section */}
        <div className="px-2 mx-auto mainCard mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Toll Free Card */}
            <div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleOpenHistory("toll")}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faPhone} className="text-blue-600 text-xl" />
                </div>
                <span className="text-gray-400 text-sm font-medium">Feb 2026</span>
              </div>
              <h3 className="text-gray-500 text-sm font-semibold uppercase tracking-wider">TOLL FREE Calls</h3>
              <p className="text-3xl font-bold text-gray-800">1,253</p>
              <p className="text-xs text-blue-500 mt-2 font-medium">Click to view 13-month history</p>
            </div>

            {/* SafetyNet Card */}
            <div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleOpenHistory("safetynet")}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-green-600 text-xl" />
                </div>
                <span className="text-gray-400 text-sm font-medium">Feb 2026</span>
              </div>
              <h3 className="text-gray-500 text-sm font-semibold uppercase tracking-wider">SAFETYNET Patients</h3>
              <p className="text-3xl font-bold text-gray-800">2,170</p>
              <p className="text-xs text-green-500 mt-2 font-medium">Click to view 13-month history</p>
            </div>

            {/* Total Toll Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faUsers} className="text-purple-600 text-xl" />
                </div>
                <span className="text-gray-400 text-sm font-medium">Overall</span>
              </div>
              <h3 className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Overall TOLL Patients</h3>
              <p className="text-3xl font-bold text-gray-800">{overallTollCount.toLocaleString()}</p>
            </div>

            {/* Total SafetyNet Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <FontAwesomeIcon icon={faChartLine} className="text-orange-600 text-xl" />
                </div>
                <span className="text-gray-400 text-sm font-medium">Overall</span>
              </div>
              <h3 className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Total SAFETYNET</h3>
              <p className="text-3xl font-bold text-gray-800">{totalSafetyNetCount.toLocaleString()}</p>
            </div>
          </div>
        </div>


        {/* Overall Activity Section */}
        <div className="px-2 mx-auto mainCard"> {/* Reduced mt-10 to mt-6 */}
          <h1
            className="text-slate-500 pb-3 text-base md:text-lg cursor-pointer"
            onClick={() => setShowOverall(!showOverall)}
          >
            Overall Activity
          </h1>
          {showOverall && (
            <div className="flex flex-row gap-x-4 overflow-hidden overflow-x-auto justify-between no-scrollbar">
              {dataOS?.map((data, index) => (
                <ScrolledCard key={index} data={data} />
              ))}
            </div>
          )}
        </div>

        <HistoryModal 
          isOpen={modalConfig.isOpen}
          onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
          title={modalConfig.title}
          data={modalConfig.data}
          label={modalConfig.label}
        />

      </main>

    </>
  );
}

export default Dashboard;
